import { useCallback, useMemo } from 'react';

import { ErrorLike, DocumentNode, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectAccountId } from '@/redux/session/selectors';
import { QueryVariables } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';
import { getAnalyticsRangeUTC, getDateRangeText } from '@/util/analytics';
import { addDaysToDate, formatDate, getDaysDifference } from '@/util/dateUtils';
import { numberFormatter, parseFilter, shouldUseSlowQueries } from '@/util/util';

import { IntervalRow } from './useInterval';

export const REQUESTS_COUNTS = gql`
  query RequestsCounts(
    $accountId: uuid
    $deskIds: _uuid
    $brainIds: _uuid
    $integrationIds: _uuid
    $brainVersions: _int4
    $channels: _text
    $startDate: timestamp
    $endDate: timestamp
    $isTest: Boolean
    $minNumUserMessages: Int
  ) {
    rows: requests_counts(
      args: {
        account_id: $accountId
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        integration_ids: $integrationIds
        brain_versions: $brainVersions
        channels: $channels
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
      }
    ) {
      counts
    }
  }
`;

export const REQUESTS_COUNTS_FAST = gql`
  query RequestsCountsFast(
    $accountId: uuid
    $deskIds: _uuid
    $brainIds: _uuid
    $startDate: timestamp
    $endDate: timestamp
    $isTest: Boolean
  ) {
    rows: requests_counts_fast(
      args: {
        account_id: $accountId
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        is_test: $isTest
      }
    ) {
      counts
    }
  }
`;

const getQueryFromVariables = (variables): DocumentNode => {
  if (shouldUseSlowQueries(variables)) {
    return REQUESTS_COUNTS;
  }
  return REQUESTS_COUNTS_FAST;
};

interface Rows {
  rows: IntervalRow[];
}

interface Usage {
  counts: number | string;
  exact_counts: number | string;
  growth: number;
  timeframe: string;
}

interface UsageHook {
  isLoading: boolean;
  isPreviousLoading?: boolean;
  currentError?: ErrorLike;
  previousError?: ErrorLike;
  refetchCurrent?: () => void;
  refetchPrevious?: () => void;
  data?: Usage;
}

interface Props {
  intervalProps: {
    value?: string;
    startDate?: string;
    endDate?: string;
    intervalInDays?: '1 day';
    is_contained?: boolean;
    is_covered?: boolean;
  };
  skip?: boolean;
  skipPrevious?: boolean;
  brainId?: string;
}

const formatUsage = (
  current?: IntervalRow[],
  previous?: IntervalRow[]
): Omit<Usage, 'timeframe'> | undefined => {
  if (!current) {
    return undefined;
  }

  let currentCounts = 0;
  let previousCounts = 0;

  if (current.length > 0) {
    currentCounts = current[0].counts;
  }
  if (previous && previous.length > 0) {
    previousCounts = previous[0].counts;
  }

  let growth = 0;
  if (previous && previousCounts > 0) {
    growth = Math.round(((currentCounts - previousCounts) / previousCounts) * 100);
  }

  return {
    counts: numberFormatter(currentCounts),
    exact_counts: numberFormatter(currentCounts, false),
    growth: previous ? growth : undefined,
  };
};

const useOverViewUsage = ({ intervalProps, skip, skipPrevious, brainId }: Props): UsageHook => {
  const accountId = useSelector(selectAccountId);
  const { startDate, endDate, is_contained, is_covered } = intervalProps;

  const filters = useSelector((state: RootState) => {
    return {
      analytics: state.analytics.accountAnalytics,
      startDate: startDate || state.analytics.startDate,
      endDate: endDate || state.analytics.endDate,
      filtersLoaded: state.analytics.filtersLoaded,
    };
  }, shallowEqual);

  const periodInDays = getDaysDifference(filters.endDate, filters.startDate);

  const previousStartDate = filters.startDate
    ? formatDate(addDaysToDate(filters.startDate, -(periodInDays + 1)), 'yyyy-MM-dd')
    : undefined;
  const previousEndDate = filters.endDate
    ? formatDate(addDaysToDate(filters.endDate, -(periodInDays + 1)), 'yyyy-MM-dd')
    : undefined;

  const location = useLocation();

  const variables = useMemo(
    () =>
      Object.assign(
        {
          ...getAnalyticsRangeUTC(filters.startDate, filters?.endDate),
          accountId,
          isCovered: is_covered ?? undefined,
          isContained: is_contained ?? undefined,
          ...(brainId ? { brainIds: `{${brainId}}`, isTest: false, isBrain: true } : {}),
        },
        ...filters.analytics
          .filter((filter) => filterFiltersByUrl(filter.type, location.pathname))
          .map((filter) => ({ [filter.type]: parseFilter(filter) }))
      ),
    [
      accountId,
      brainId,
      filters.analytics,
      filters.endDate,
      filters.startDate,
      is_contained,
      is_covered,
      location.pathname,
    ]
  );

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
    refetch: refetchCurrent,
  } = useQuery<Rows, QueryVariables>(getQueryFromVariables(variables), {
    variables,
    skip: skip || !accountId,
  });

  const {
    loading: previousLoading,
    data: previousData,
    error: previousError,
    refetch: refetchPrevious,
  } = useQuery<Rows, QueryVariables>(getQueryFromVariables(variables), {
    variables: { ...variables, ...getAnalyticsRangeUTC(previousStartDate, previousEndDate) },
    skip: skipPrevious || !filters.filtersLoaded,
  });
  const currentRows = currentData?.rows;
  const previousRows = previousData?.rows;

  const data = useMemo(() => {
    return {
      ...formatUsage(currentRows, previousRows),
      timeframe: getDateRangeText(filters.startDate, filters.endDate),
    };
  }, [currentRows, filters.endDate, filters.startDate, previousRows]);

  const onRefetchCurrent = useCallback(() => {
    refetchCurrent(variables);
  }, [refetchCurrent, variables]);

  const onRefetchPrevious = useCallback(() => {
    refetchPrevious({ ...variables, ...getAnalyticsRangeUTC(previousStartDate, previousEndDate) });
  }, [previousEndDate, previousStartDate, refetchPrevious, variables]);

  if (currentLoading) {
    return { isLoading: true };
  }

  if (previousLoading && !skipPrevious) {
    return { data, isLoading: false, isPreviousLoading: true };
  }

  if (currentError || previousError) {
    return {
      isLoading: false,
      currentError,
      previousError,
      refetchCurrent: onRefetchCurrent,
      refetchPrevious: onRefetchPrevious,
    };
  }

  return { isLoading: false, data };
};

export default useOverViewUsage;
